import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import SchemaOrg from '../../../components/base/SchemaOrg';

export default function WaterTankPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const pageTitle = '桃園水塔清洗服務｜專業清洗消毒｜永業環保';
    const pageDesc = '永業環保提供專業水塔清洗消毒服務，使用環保署認證清潔劑，確保水質純淨安全。服務桃園、新北、台北、新竹地區，20年經驗，政府認證，提供完整清洗報告與水質檢測。';
    const pageKeywords = '水塔清洗,水塔消毒,水塔清潔,水塔維護,水質檢測,桃園水塔';
    const pageUrl = `${siteUrl}/services/water-tank`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);
  }, [siteUrl]);

  const faqItems = [
    {
      question: '水塔多久需要清洗一次？',
      answer: '根據環保署建議，水塔至少每半年清洗一次。如果水塔位於空氣污染較嚴重的地區，或是水質明顯變差，建議每3-4個月清洗一次。定期清洗可以確保用水安全，預防細菌滋生。'
    },
    {
      question: '水塔清洗需要多久時間？',
      answer: '一般住宅水塔（5噸以下）清洗時間約2-3小時，包含排水、刷洗、消毒、注水等步驟。大型水塔或社區水塔可能需要4-6小時。我們會在施工前評估時間，並配合您的時間安排。'
    },
    {
      question: '清洗水塔會停水嗎？',
      answer: '是的，清洗期間需要暫時停水。我們建議您事先儲水備用，或安排在用水較少的時段進行。清洗完成後會立即恢復供水，整個過程不會影響太久。'
    },
    {
      question: '你們使用什麼清潔劑？',
      answer: '我們使用環保署認證的食品級清潔劑和消毒劑，對人體無害，不會殘留有害物質。清洗後會徹底沖洗，確保水質安全。所有使用的藥劑都符合飲用水標準。'
    },
    {
      question: '水塔清洗費用如何計算？',
      answer: '費用依水塔容量、高度、污染程度而定。一般住宅水塔（5噸以下）約$2,500-$3,500，大型水塔或社區水塔需現場評估。我們提供免費到府估價，報價透明，絕不額外加價。'
    },
    {
      question: '清洗後會提供證明嗎？',
      answer: '是的，我們會提供完整的清洗報告，包含清洗前後照片、水質檢測報告、消毒證明等。如果您需要向主管機關申報，我們也可以提供正式的清洗證明文件。'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/services/water-tank#service`,
      name: '水塔清洗服務',
      description: '專業水塔清洗消毒服務，使用環保署認證清潔劑，確保水質純淨安全。提供完整清洗報告與水質檢測。',
      provider: {
        '@type': 'LocalBusiness',
        name: '永業環保',
        telephone: '0800-281-281',
        url: siteUrl
      },
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      offers: {
        '@type': 'Offer',
        priceRange: 'NT$2,500-NT$5,000',
        priceCurrency: 'TWD'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/services/water-tank#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/services/water-tank#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '服務項目', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: '水塔清洗', item: `${siteUrl}/services/water-tank` }
      ]
    }
  ];

  const steps = [
    {
      icon: 'ri-phone-line',
      title: '預約檢查',
      description: '聯繫我們安排現場勘查，評估水塔狀況與清洗需求'
    },
    {
      icon: 'ri-calendar-check-line',
      title: '排定時間',
      description: '確認清洗日期與時間，提前做好準備工作'
    },
    {
      icon: 'ri-tools-line',
      title: '專業清洗',
      description: '使用環保藥劑與專業設備，徹底清潔水塔內部'
    },
    {
      icon: 'ri-shield-check-line',
      title: '消毒檢測',
      description: '完成消毒作業並進行水質檢測，確保飲用安全'
    },
    {
      icon: 'ri-file-text-line',
      title: '完工報告',
      description: '提供清洗前後對比照片與完整施工報告'
    }
  ];

  const targets = [
    {
      icon: 'ri-building-line',
      title: '住宅大樓',
      description: '社區大樓、公寓水塔定期清洗維護'
    },
    {
      icon: 'ri-store-line',
      title: '商業場所',
      description: '辦公大樓、商場、飯店水塔清潔'
    },
    {
      icon: 'ri-hospital-line',
      title: '醫療機構',
      description: '醫院、診所等高標準水質要求場所'
    },
    {
      icon: 'ri-restaurant-line',
      title: '餐飲業者',
      description: '餐廳、食品工廠水塔衛生維護'
    },
    {
      icon: 'ri-government-line',
      title: '公家機關',
      description: '學校、政府單位水塔清洗服務'
    },
    {
      icon: 'ri-home-line',
      title: '透天住宅',
      description: '獨棟住宅、別墅水塔清潔保養'
    }
  ];

  const pricing = [
    {
      capacity: '1噸以下',
      price: '2,500',
      features: ['基礎清洗', '消毒作業', '水質檢測', '施工報告']
    },
    {
      capacity: '1-3噸',
      price: '3,500',
      features: ['深度清洗', '消毒作業', '水質檢測', '施工報告', '管線檢查'],
      popular: true
    },
    {
      capacity: '3-5噸',
      price: '5,000',
      features: ['深度清洗', '消毒作業', '水質檢測', '施工報告', '管線檢查', '定期追蹤']
    },
    {
      capacity: '5噸以上',
      price: '面議',
      features: ['客製化服務', '專案報價', '長期合約優惠']
    }
  ];

  const faqs = [
    {
      question: '水塔多久需要清洗一次？',
      answer: '根據環保署規定，水塔至少每半年清洗一次。但實際清洗頻率應視使用狀況、水質、環境等因素調整。建議住宅大樓每 6 個月、餐飲業每 3-4 個月清洗一次，以確保用水安全。'
    },
    {
      question: '清洗水塔需要多少時間？',
      answer: '一般住宅水塔（1-3噸）清洗時間約 2-3 小時，包含排水、清洗、消毒、注水等步驟。大型水塔或狀況較差的水塔可能需要 4-6 小時。我們會在現場勘查時告知預估時間。'
    },
    {
      question: '清洗時需要停水嗎？',
      answer: '是的，清洗期間需要暫停供水。我們會協助您安排適當的清洗時間，將對日常生活的影響降到最低。建議選擇用水較少的時段進行清洗作業。'
    },
    {
      question: '使用的清潔劑安全嗎？',
      answer: '我們使用符合環保署認證的環保清潔劑，對人體無害且不會殘留。清洗完成後會進行多次沖洗，並使用食品級消毒劑消毒，確保水質安全無虞。'
    },
    {
      question: '清洗後水質會改善嗎？',
      answer: '是的！定期清洗可以有效去除水塔內的沉澱物、青苔、細菌等污染物，大幅改善水質。清洗後的水會更清澈、無異味，也能避免水管堵塞等問題。'
    },
    {
      question: '如何確認清洗品質？',
      answer: '我們會提供清洗前後的對比照片、水質檢測報告，以及完整的施工紀錄。清洗完成後，您可以現場確認水塔內部清潔狀況，我們也提供施工保固服務。'
    }
  ];

  const relatedServices = [
    {
      title: '水管水刀疏通',
      description: '高壓水刀技術，快速疏通各種管線堵塞',
      icon: 'ri-water-flash-line',
      link: '/services/pipe-cleaning'
    },
    {
      title: '內視鏡檢查',
      description: '精密檢測管線內部狀況，找出問題根源',
      icon: 'ri-search-eye-line',
      link: '/services/endoscope'
    },
    {
      title: '水溝清洗',
      description: '專業清理排水溝渠，預防淹水問題',
      icon: 'ri-contrast-drop-line',
      link: '/services/drain-cleaning'
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 pt-32 pb-20 md:pt-32 md:pb-20 py-20">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/1f0ba830905b73ecd85f1d94ad84ce44.jpg')] bg-cover bg-center opacity-5"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-drop-line"></i>
                <span>專業水塔清洗服務</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 font-serif">
                水塔清洗服務
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                定期清洗水塔，守護家人健康。使用環保藥劑與專業設備，<br className="hidden md:block" />
                徹底清除水塔內的污垢、青苔與細菌，確保飲用水安全無虞
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>立即預約清洗</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 border-2 border-teal-600 whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>0800-281-281</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Service Introduction */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 font-serif">
                    為什麼需要定期清洗水塔？
                  </h2>
                  <div className="space-y-4 text-gray-600 leading-relaxed">
                    <p>
                      水塔是家庭用水的重要儲存設備，但長期使用下，水塔內部容易累積<strong>沉澱物、青苔、細菌</strong>等污染物，不僅影響水質，更可能危害家人健康。
                    </p>
                    <p>
                      根據環保署規定，水塔應<strong>每半年清洗一次</strong>。永業環保擁有 20 年專業經驗，使用環保認證藥劑與先進設備，提供完整的水塔清洗服務，讓您安心使用每一滴水。
                    </p>
                    <div className="grid grid-cols-2 gap-4 mt-8">
                      <div className="bg-teal-50 p-4 rounded-lg">
                        <div className="text-3xl font-bold text-teal-600 mb-1">20+</div>
                        <div className="text-sm text-gray-600">年專業經驗</div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600 mb-1">5000+</div>
                        <div className="text-sm text-gray-600">服務案例</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-3xl font-bold text-green-600 mb-1">100%</div>
                        <div className="text-sm text-gray-600">環保藥劑</div>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="text-3xl font-bold text-orange-600 mb-1">政府</div>
                        <div className="text-sm text-gray-600">合格認證</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20technician%20cleaning%20water%20tank%20interior%20with%20safety%20equipment%2C%20wearing%20protective%20gear%20and%20using%20professional%20cleaning%20tools%2C%20clean%20and%20bright%20environment%2C%20water%20tank%20interior%20visible%2C%20high%20quality%20professional%20photography%2C%20realistic%20work%20scene%2C%20emphasis%20on%20cleanliness%20and%20safety%2C%20natural%20lighting&width=600&height=700&seq=water-tank-cleaning-work&orientation=portrait"
                    alt="專業水塔清洗作業"
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl hidden md:block">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                        <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">政府認證</div>
                        <div className="font-bold text-gray-900">合格業者</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cleaning Steps */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  專業清洗流程
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  五大步驟，確保水塔清潔徹底、用水安全
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {steps.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                      <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                        <i className={`${step.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-bold text-teal-600 mb-2">
                          步驟 {index + 1}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                        <i className="ri-arrow-right-line text-2xl text-teal-400"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Target Customers */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  適用對象
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  各類場所水塔清洗，我們都能提供專業服務
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {targets.map((target, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors duration-300">
                      <i className={`${target.icon} text-3xl text-teal-600 group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {target.title}
                    </h3>
                    <p className="text-gray-600">
                      {target.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-teal-50 via-white to-blue-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  價格參考
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  透明公開的收費標準，依水塔容量計價
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {pricing.map((plan, index) => (
                  <div
                    key={index}
                    className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                      plan.popular ? 'ring-2 ring-teal-500' : ''
                    }`}
                  >
                    {plan.popular && (
                      <div className="bg-gradient-to-r from-teal-500 to-blue-500 text-white text-center py-2 text-sm font-medium">
                        最受歡迎
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {plan.capacity}
                      </h3>
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-teal-600">
                          {plan.price === '面議' ? plan.price : `$${plan.price}`}
                        </span>
                        {plan.price !== '面議' && (
                          <span className="text-gray-500 ml-1">起</span>
                        )}
                      </div>
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-check-line text-teal-600"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-6 pt-0">
                      <Link
                        to="/contact"
                        className={`block text-center py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${
                          plan.popular
                            ? 'bg-teal-600 text-white hover:bg-teal-700'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        立即預約
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center text-sm text-gray-500">
                * 實際價格依現場狀況評估，特殊情況另行報價
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  常見問題
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  關於水塔清洗的疑問，這裡都有解答
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-4 md:p-6 rounded-xl border border-gray-100 hover:border-teal-200 transition-all duration-300 group"
                  >
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <h3 className="text-base md:text-lg font-bold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <i className="ri-arrow-down-s-line text-2xl text-teal-600 group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  相關服務
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  更多專業服務，滿足您的需求
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-3xl text-teal-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-teal-600 font-medium">
                      <span>了解更多</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-teal-600 to-blue-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20water%20flow%20pattern%20with%20clean%20droplets%20and%20waves%2C%20minimalist%20design%2C%20soft%20gradient%20background%2C%20light%20and%20fresh%20atmosphere%2C%20modern%20abstract%20style%2C%20high%20quality%20digital%20art%2C%20teal%20and%20blue%20color%20scheme%2C%20flowing%20water%20texture&width=1920&height=400&seq=water-tank-cta-bg&orientation=landscape')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">
                守護家人健康，從清潔水塔開始
              </h2>
              <p className="text-lg md:text-xl mb-8 opacity-90">
                立即預約永業環保專業水塔清洗服務<br className="hidden md:block" />
                讓您安心使用每一滴水
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>線上預約</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent text-white px-8 py-4 rounded-lg font-medium hover:bg-white/10 transition-all duration-300 border-2 border-white whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>電話諮詢</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}